'use client';

import { FC } from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Container } from '@purpur/library';

import { BlockArticlesType } from '../../models/Content';
import { getBlockId } from '../../utils/content';
import { ArticleCard } from '../ArticleCard/ArticleCard';
import { BlockHeader } from '../common/BlockHeader';
import {
    ArticleCardsCarousel,
    ArticleCardsGrid,
    DisplayOptionController,
    StyledBlockArticles,
} from './BlockArticles.styles';
import { BlockArticlesData } from './model';
import { transformToBlockArticles } from './transformers';

const getMaxArticles = ({ articles, displayOption }: BlockArticlesData) => {
    switch (displayOption) {
        case '1:1:1':
        case '1:2':
            return articles.slice(0, 3);
        case '1:3':
            return articles.slice(0, 4);
        case 'standalone':
            return articles.slice(0, 1);
        default:
            return [];
    }
};

type Props = {
    contentItem: BlockArticlesType;
};

export const BlockArticles: FC<Props> = ({ contentItem: initialContentItem }) => {
    const contentItem = useContentfulLiveUpdates(initialContentItem);
    const blockArticlesData = transformToBlockArticles(contentItem);
    const { colorOption, displayOption, cta, title } = blockArticlesData;
    const articles = getMaxArticles(blockArticlesData);

    if (articles.length === 0) {
        return null;
    }

    const cardVariant =
        (colorOption === 'color-light-beige' && 'primary') ||
        (colorOption === 'color-no-background' && 'secondary') ||
        'primary';

    const cards = articles.map((article) => (
        <ArticleCard
            key={article.id}
            content={article}
            variant={cardVariant}
            titleTag={title ? 'h3' : 'h2'}
            role="listitem"
        />
    ));

    return (
        <StyledBlockArticles $colorOption={colorOption} id={getBlockId(contentItem)}>
            <Container>
                <BlockHeader title={title} cta={cta} />
                <DisplayOptionController $displayOption={displayOption}>
                    <ArticleCardsCarousel slides={cards} />
                    <ArticleCardsGrid aria-label={title}>{cards}</ArticleCardsGrid>
                </DisplayOptionController>
            </Container>
        </StyledBlockArticles>
    );
};
